import React, {
    useId,
    useState,
    useRef,
    useEffect,
    useInsertionEffect,
    useLayoutEffect,
    useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { Control, FieldPath, FieldValues } from 'react-hook-form';
import BaseFormComponent, {
    type RequiredIndicator,
    type RightSideContent,
    useFormFieldIds,
} from './BaseFormComponent';
import CaretDownIcon from './CustomIcons/CaretDownIcon';
import CloseIcon from './CustomIcons/CloseIcon';
import FieldMessage from './FieldMessage';
import LoadingInput from './LoadingInput';
import Spinner from './Spinner';
import Tooltip from './Tooltip';
import { CONTROL_TEXT_SIZE, CONTROL_TEXT_SIZE_CLASS } from './controlTextSize';
import { controlWidthStyle } from './controlWidth';
import { DROPDOWN_TRIGGER_ATTR, type DropdownTone, getTriggerVisual } from './dropdownTriggerStyle';
import { pushEscapeLayer } from './escapeLayers';
import {
    MENU_MAX_HEIGHT_PX,
    MENU_SPECS,
    type MenuGeometry,
    isTriggerOffscreen,
    measureMenuGeometry,
    rectSignature,
} from './floatingMenuGeometry';
import { ensureDropdownPanelStyles } from './optionRowStyles';
import { overlayIdentity } from './portalOverlay';
import { useTopLayer } from './topLayer';

export type { DropdownTone };

export interface DropdownItem {
    id: string;
    label: string;
    icon?: React.ReactNode;
    /** Alternate label shown in the closed trigger when selected (falls back to `label`). */
    triggerLabel?: string;
    /** Items sharing a `group` render under a section header, in first-seen order. */
    group?: string;
    subtitle?: string;
    rightSideText?: string;
    rightSideTextVariant?: 'muted' | 'primary';
    rightSideTextAlignment?: 'left' | 'right';
}

interface DropdownBaseProps {
    label?: string;
    hint?: string;
    rightSideContent?: RightSideContent;
    tooltip?: string;
    name: FieldPath<FieldValues> | string;
    loading?: boolean;
    menuLoading?: boolean;
    items: DropdownItem[];
    onItemSelected?: (id?: string) => void;
    defaultValue?: string;
    /** Controlled selection. When set, the trigger always reflects this id and the component
     *  keeps no selection state of its own. Pass it with `onItemSelected`. */
    value?: string;
    disabled?: boolean;
    error?: boolean;
    /** `pending` marks a value the user still has to supply. Outranks open and selection. */
    tone?: DropdownTone;
    className?: string;
    formControl?: Control<FieldValues, unknown>;
    description?: string;
    required?: boolean;
    requiredIndicator?: RequiredIndicator;
    helperText?: React.ReactNode;
    placeholder?: string;
    subtitleLayout?: SubtitleLayout;
    /** Adds a clear control to the trigger, reported as `onItemSelected(undefined)`. */
    clearable?: boolean;
    /** 'large' (42px) for form inputs; 'medium' (32px) for toolbar chips; 'small' (28px) beside a title. */
    size?: DropdownSize;
    fullWidth?: boolean;
    width?: string | number;
    /**
     * Raises the open menu's floor above the shared 160px one, without widening the trigger.
     * For a control pinned narrow by its column — a role picker in a 160px cell, a member
     * picker in a 200px slot — whose options carry a subtitle the trigger never has to show.
     */
    minWidth?: number;
    /** Names the trigger from the light DOM, where `htmlFor` cannot reach into the shadow root. */
    id?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
}

/**
 * The search trigger is a single-line text input with nowhere for a second line, so
 * `showSubtitleInTrigger` is rejected alongside `search` rather than silently ignored.
 */
type DropdownTriggerModeProps =
    | {
          search?: false;
          /** Promotes the selected item's `subtitle` into the closed trigger, beneath the value. */
          showSubtitleInTrigger?: boolean;
      }
    | { search: true; showSubtitleInTrigger?: never };

export type DropdownProps = DropdownBaseProps & DropdownTriggerModeProps;

/**
 * Matches Button and the Figma component library: 28 / 32 / 42px. These names moved up a
 * tier in 0.71 — what was `xsmall`/`small`/`medium` is now `small`/`medium`/`large`.
 */
export type DropdownSize = 'small' | 'medium' | 'large';

export type SubtitleLayout = 'inline' | 'stacked';

const PANEL_MESSAGE_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '24px 16px',
    color: 'var(--malachite-text-dimmed, rgb(161, 161, 161))',
};

// Figma pins the option's inline annotation at 11px on every tier — unlike the label,
// it does not ride the ladder (Item Large 68:2617, Item Medium 2343:2134).
const ANNOTATION_FONT_SIZE = 'var(--malachite-font-size-xsmall, 11px)';

const OPTION_SELECTOR = '[data-dropdown-option]';

const panelOptions = (panel: HTMLElement | null): HTMLElement[] =>
    Array.from(panel?.querySelectorAll<HTMLElement>(OPTION_SELECTOR) ?? []);

const matchesSearch = (item: DropdownItem, query: string) => {
    const q = query.toLowerCase();
    const labelMatches = item.label.toLowerCase().includes(q);
    const subtitleMatches = item.subtitle ? item.subtitle.toLowerCase().includes(q) : false;
    const rightSideTextMatches = item.rightSideText
        ? item.rightSideText.toLowerCase().includes(q)
        : false;
    return labelMatches || subtitleMatches || rightSideTextMatches;
};

const TRIGGER_SIZE_HEIGHT: Record<DropdownSize, string> = {
    small: 'h-[var(--malachite-control-height-small,28px)]',
    medium: 'h-[var(--malachite-control-height-medium,32px)]',
    large: 'h-[var(--malachite-control-height-large,42px)]',
};

const TRIGGER_SIZE_CLASSES: Record<DropdownSize, string> = {
    small: `${TRIGGER_SIZE_HEIGHT.small} px-2.5 py-1.5 ${CONTROL_TEXT_SIZE_CLASS.small}`,
    medium: `${TRIGGER_SIZE_HEIGHT.medium} px-3 py-1.5 ${CONTROL_TEXT_SIZE_CLASS.medium}`,
    large: `${TRIGGER_SIZE_HEIGHT.large} px-4 py-2.5 ${CONTROL_TEXT_SIZE_CLASS.large}`,
};

const TRIGGER_SIZE_STACKED_CLASSES: Record<DropdownSize, string> = {
    small: `min-h-[var(--malachite-control-height-small,28px)] px-2.5 py-1.5 ${CONTROL_TEXT_SIZE_CLASS.small}`,
    medium: `min-h-[var(--malachite-control-height-medium,32px)] px-3 py-1.5 ${CONTROL_TEXT_SIZE_CLASS.medium}`,
    large: `min-h-[var(--malachite-control-height-large,42px)] px-4 py-2.5 ${CONTROL_TEXT_SIZE_CLASS.large}`,
};

const CLEAR_OFFSET_CLASSES: Record<DropdownSize, string> = {
    small: 'right-[26px] size-4',
    medium: 'right-[30px] size-4',
    large: 'right-[38px] size-4',
};

const CLEAR_STACKED_TOP_CLASSES: Record<DropdownSize, string> = {
    small: 'top-[calc(var(--malachite-control-height-small,28px)/2)] -translate-y-1/2',
    medium: 'top-[calc(var(--malachite-control-height-medium,32px)/2)] -translate-y-1/2',
    large: 'top-[calc(var(--malachite-control-height-large,42px)/2)] -translate-y-1/2',
};

// Radius tracks the tier in Figma: 10 / 12 / 14. Only 12 has a --malachite-radius-* rung.
const TRIGGER_SIZE_RADIUS: Record<DropdownSize, string> = {
    small: 'rounded-[var(--malachite-radius-10,10px)]',
    medium: 'rounded-xl',
    large: 'rounded-[var(--malachite-radius-xxl,14px)]',
};

const NEVER = () => false;

type TruncatedTriggerLines = 'value' | 'subtitle' | 'both';

// Portaled to document.body, outside the shadow root: styling here has to be inline.
const TOOLTIP_LINES_STYLE: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
};

const triggerTooltipText = (
    lines: TruncatedTriggerLines,
    valueText: React.ReactNode,
    subtitle: React.ReactNode,
): React.ReactNode => {
    if (lines === 'value') {
        return valueText;
    }
    if (lines === 'subtitle') {
        return subtitle;
    }
    return (
        <span style={TOOLTIP_LINES_STYLE}>
            <span>{valueText}</span>
            <span>{subtitle}</span>
        </span>
    );
};

const ValueRow: React.FC<{ wrap: boolean; id?: string; children: React.ReactNode }> = ({
    wrap,
    id,
    children,
}) =>
    wrap ? (
        <span id={id} className="min-w-0 flex items-center gap-2">
            {children}
        </span>
    ) : (
        <>{children}</>
    );

const LabelRow: React.FC<{ row: boolean; children: React.ReactNode }> = ({ row, children }) =>
    row ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {children}
        </span>
    ) : (
        <>{children}</>
    );

interface DropdownTriggerProps {
    isOpen: boolean;
    onClick: () => void;
    selectedItem: DropdownItem | undefined;
    hasClear?: boolean;
    onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    clearLabel?: string;
    disabled?: boolean;
    error?: boolean;
    tone?: DropdownTone;
    placeholder?: string;
    search?: boolean;
    showSubtitleInTrigger?: boolean;
    searchQuery?: string;
    onSearchChange?: (value: string) => void;
    onFocusOpen?: () => void;
    /** ArrowDown on the trigger: opens the menu, or steps into it if already open. */
    onArrowDown?: () => void;
    size?: DropdownSize;
    controlId: string;
    labelId?: string;
    describedBy?: string;
    required?: boolean;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    /** A caller-supplied id means an external `<label for>` may be naming the control. */
    hasCallerId?: boolean;
}

const DropdownTrigger = React.forwardRef<HTMLElement, DropdownTriggerProps>(
    (
        {
            isOpen,
            onClick,
            selectedItem,
            disabled = false,
            error = false,
            tone,
            placeholder,
            search = false,
            showSubtitleInTrigger = false,
            searchQuery = '',
            onSearchChange,
            onFocusOpen,
            onArrowDown,
            size = 'large',
            hasClear = false,
            onClear,
            clearLabel,
            controlId,
            labelId,
            describedBy,
            required = false,
            ariaLabel,
            ariaLabelledBy,
            hasCallerId = false,
        },
        forwardedRef,
    ) => {
        const stacked = showSubtitleInTrigger && !search && !!selectedItem?.subtitle;
        const sizeClasses = stacked
            ? TRIGGER_SIZE_STACKED_CLASSES[size]
            : TRIGGER_SIZE_CLASSES[size];
        const sizeRadius = TRIGGER_SIZE_RADIUS[size];
        const valueId = `${controlId}-value`;
        const subtitleId = `${controlId}-subtitle`;
        // aria-labelledby outranks aria-label in the accessible-name algorithm, so the
        // generated label+value pair has to be dropped for a caller's aria-label to apply.
        // Named off nothing, the button takes its name from every descendant — the subtitle
        // included, which `aria-describedby` then repeats. Naming it off the value alone fixes
        // that, but only where nothing else already names the control.
        const namesItself = !labelId && !ariaLabel && !hasCallerId && stacked;
        const labelledBy =
            ariaLabelledBy ??
            (labelId && !ariaLabel ? `${labelId} ${valueId}` : undefined) ??
            (namesItself ? valueId : undefined);

        const handleArrowDown = (event: React.KeyboardEvent): void => {
            if (event.key !== 'ArrowDown') {
                return;
            }
            event.preventDefault();
            onArrowDown?.();
        };

        const valueTextRef = useRef<HTMLSpanElement>(null);
        const subtitleTextRef = useRef<HTMLSpanElement>(null);
        const triggerVisual = getTriggerVisual({ disabled, error, tone, open: isOpen });
        const showValueLabel = !isOpen && !!selectedItem;
        const valueText = selectedItem?.triggerLabel ?? selectedItem?.label;
        const triggerSubtitle = stacked ? selectedItem?.subtitle : undefined;
        const isValueTruncated = () => {
            const el = valueTextRef.current;
            return !!el && el.scrollWidth > el.clientWidth;
        };
        const isSubtitleTruncated = () => {
            const el = subtitleTextRef.current;
            return !!el && el.scrollWidth > el.clientWidth;
        };

        // A tooltip reads its text at render, but which line is cut off is only knowable on
        // hover — so the one measurement pass that decides whether to show also records what
        // to say. The initial value never shows: untruncated, the tooltip refuses to open.
        const [truncatedLines, setTruncatedLines] = useState<TruncatedTriggerLines>('subtitle');
        const measureTruncatedLines = () => {
            const value = isValueTruncated();
            const subtitle = isSubtitleTruncated();
            if (!value && !subtitle) {
                return false;
            }
            setTruncatedLines(value && subtitle ? 'both' : value ? 'value' : 'subtitle');
            return true;
        };

        if (search && !disabled) {
            return (
                <div
                    ref={forwardedRef as React.Ref<HTMLDivElement>}
                    className={`
                relative w-full ${sizeClasses} ${sizeRadius}
                border border-solid
                transition-all duration-200
                flex items-center
            `}
                    style={triggerVisual}
                    {...{ [DROPDOWN_TRIGGER_ATTR]: '' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {selectedItem?.icon && (
                        <span className="flex shrink-0 items-center justify-center pr-2">
                            {selectedItem.icon}
                        </span>
                    )}
                    <Tooltip text={valueText} shouldShow={isValueTruncated} isShrinkable fill>
                        <div className="relative flex-1 flex items-center min-w-0">
                            {showValueLabel && (
                                <span
                                    aria-hidden="true"
                                    className="absolute inset-0 flex items-center pointer-events-none"
                                >
                                    <span
                                        ref={valueTextRef}
                                        className="min-w-0 block truncate text-[length:inherit]
                                        text-[var(--malachite-text-primary)]"
                                    >
                                        {valueText}
                                    </span>
                                </span>
                            )}
                            <input
                                id={controlId}
                                type="text"
                                aria-label={ariaLabel}
                                aria-labelledby={ariaLabelledBy}
                                aria-invalid={error || undefined}
                                aria-required={required || undefined}
                                aria-describedby={describedBy}
                                value={isOpen ? searchQuery : (selectedItem?.label ?? '')}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onSearchChange?.(e.target.value);
                                    if (!isOpen) {
                                        onClick();
                                    }
                                }}
                                onFocus={() => {
                                    onFocusOpen?.();
                                }}
                                onKeyDown={handleArrowDown}
                                placeholder={placeholder ?? 'Select Field'}
                                className={`flex-1 min-w-0 bg-transparent outline-none
                            text-[length:inherit] font-normal
                            placeholder:text-[var(--malachite-text-dimmed)] ${
                                showValueLabel
                                    ? 'text-transparent'
                                    : 'text-[var(--malachite-text-primary)]'
                            }`}
                            />
                        </div>
                    </Tooltip>
                    {hasClear && (
                        <button
                            type="button"
                            aria-label={clearLabel}
                            onClick={onClear}
                            className="flex shrink-0 items-center justify-center pl-2 cursor-pointer
                                bg-transparent border-none p-0 text-[var(--malachite-text-placeholder)]
                                hover:text-[var(--malachite-text-primary)] transition-colors
                                rounded-full focus-visible:[outline:2px_solid_var(--malachite-text-primary)] focus-visible:outline-offset-2"
                        >
                            <CloseIcon size={13} />
                        </button>
                    )}
                    <div className="flex items-center justify-center pl-2">
                        {/* Mouse-only affordance: the input beside it already opens the menu on
                            click and focus, so this stays out of the tab order to avoid a
                            duplicate stop — but it must still be a button, not a click-handling
                            span, so it is exposed and activatable rather than invisible. */}
                        <button
                            type="button"
                            tabIndex={-1}
                            aria-label={isOpen ? 'Close options' : 'Open options'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClick();
                            }}
                            className="flex items-center justify-center cursor-pointer bg-transparent border-none p-0"
                        >
                            <CaretDownIcon
                                size={13}
                                style={{
                                    color: isOpen
                                        ? 'var(--malachite-text-primary, rgb(10, 10, 10))'
                                        : 'var(--malachite-text-placeholder, rgb(187, 187, 187))',
                                    transition: 'transform 0.2s ease, color 0.2s ease',
                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        </button>
                    </div>
                </div>
            );
        }

        const trigger = (
            <button
                ref={forwardedRef as React.Ref<HTMLButtonElement>}
                id={controlId}
                type="button"
                aria-label={ariaLabel}
                aria-labelledby={labelledBy}
                aria-invalid={error || undefined}
                aria-required={required || undefined}
                aria-describedby={
                    [describedBy, triggerSubtitle ? subtitleId : undefined]
                        .filter(Boolean)
                        .join(' ') || undefined
                }
                onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                }}
                onKeyDown={handleArrowDown}
                disabled={disabled}
                style={triggerVisual}
                {...{ [DROPDOWN_TRIGGER_ATTR]: '' }}
                className={`
                relative w-full ${sizeClasses} ${sizeRadius}
                border border-solid
                transition-all duration-200
                flex ${stacked ? 'items-start' : 'items-center'} gap-3
                cursor-pointer
                disabled:cursor-not-allowed
            `}
            >
                {/* Spans: <button> takes phrasing content only. */}
                <span
                    id={triggerSubtitle ? undefined : valueId}
                    className={`text-left flex-1 min-w-0 flex ${
                        triggerSubtitle ? 'flex-col gap-1' : 'items-center gap-2'
                    }`}
                >
                    {selectedItem ? (
                        <>
                            <ValueRow wrap={!!triggerSubtitle} id={valueId}>
                                {selectedItem.icon && (
                                    <span className="flex items-center justify-center shrink-0">
                                        {selectedItem.icon}
                                    </span>
                                )}
                                <Tooltip
                                    text={valueText}
                                    shouldShow={triggerSubtitle ? NEVER : isValueTruncated}
                                    triggerAs={triggerSubtitle ? 'span' : 'div'}
                                    isShrinkable
                                >
                                    <span
                                        ref={valueTextRef}
                                        className={`min-w-0 block truncate text-[length:inherit] ${
                                            disabled
                                                ? 'text-[var(--malachite-text-placeholder)]'
                                                : 'text-[var(--malachite-text-primary)]'
                                        }`}
                                    >
                                        {valueText}
                                    </span>
                                </Tooltip>
                            </ValueRow>
                            {triggerSubtitle && (
                                <span
                                    id={subtitleId}
                                    ref={subtitleTextRef}
                                    className="min-w-0 block truncate text-[length:var(--malachite-font-size-xsmall,11px)] text-[var(--malachite-text-secondary,rgb(123,123,123))] leading-normal"
                                >
                                    {triggerSubtitle}
                                </span>
                            )}
                        </>
                    ) : (
                        <span
                            className={`flex-1 min-w-0 truncate text-[length:inherit] ${
                                disabled
                                    ? 'text-[var(--malachite-text-placeholder)]'
                                    : 'text-[var(--malachite-text-dimmed)]'
                            }`}
                        >
                            {placeholder ?? 'Select Field'}
                        </span>
                    )}
                </span>
                {hasClear && <span aria-hidden="true" className="shrink-0 w-4" />}
                <span className="flex items-center justify-center shrink-0">
                    <CaretDownIcon
                        size={13}
                        style={{
                            color: isOpen
                                ? 'var(--malachite-text-primary, rgb(10, 10, 10))'
                                : 'var(--malachite-text-placeholder, rgb(187, 187, 187))',
                            transition: 'transform 0.2s ease, color 0.2s ease',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                </span>
            </button>
        );

        // The button is what takes focus, so the tooltip wraps it rather than a line inside:
        // mounted on a line it could only ever open with a pointer, and nested tooltips opened
        // together on hover. Its text is already in the trigger's name and description, so it
        // repeats it visually only. `fill`, not `isShrinkable`, whose `overflow: hidden` would
        // clip the error ring the button paints outside its border box.
        // Gated on the prop, not on there being a subtitle to show: keyed off the selection the
        // wrapper would appear on the first choice, remounting the button `focusTrigger` has
        // just handed focus back to and dropping the keyboard user at <body>.
        return showSubtitleInTrigger && !search ? (
            <Tooltip
                text={triggerTooltipText(truncatedLines, valueText, triggerSubtitle)}
                shouldShow={triggerSubtitle ? measureTruncatedLines : NEVER}
                fill
                describesTrigger={false}
            >
                {trigger}
            </Tooltip>
        ) : (
            trigger
        );
    },
);
DropdownTrigger.displayName = 'DropdownTrigger';

interface DropdownItemComponentProps {
    item: DropdownItem;
    selected: boolean;
    subtitleLayout: SubtitleLayout;
    onItemSelected: (item: DropdownItem) => void;
}

const DropdownItemComponent = ({
    item,
    selected,
    subtitleLayout,
    onItemSelected,
}: DropdownItemComponentProps) => {
    const textColor = selected
        ? 'var(--malachite-accent-foreground, rgb(2, 122, 66))'
        : 'var(--malachite-text-primary, rgb(10, 10, 10))';
    const stacked = subtitleLayout === 'stacked' && !!item.subtitle;
    const optionId = `mal-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const optionLabelId = `${optionId}-label`;
    const optionSubtitleId = `${optionId}-subtitle`;
    const subtitleRef = useRef<HTMLSpanElement>(null);
    const isSubtitleTruncated = () => {
        const el = subtitleRef.current;
        return !!el && el.scrollWidth > el.clientWidth + 1;
    };

    // Default to muted so right-side metadata (e.g. category tags) de-emphasises
    // against the primary item label. Consumers can opt back in with 'primary'.
    const rightSideTextColor =
        item.rightSideTextVariant === 'primary'
            ? 'var(--malachite-text-right-side-text-primary, rgb(10, 10, 10))'
            : 'var(--malachite-text-right-side-text-muted, rgb(123, 123, 123))';

    const rightSideJustify = item.rightSideTextAlignment === 'left' ? 'flex-start' : 'flex-end';

    const option = (
        // `role="option"` would be invalid — nothing here declares a `listbox`, and an
        // orphaned option is dropped from the a11y tree. `aria-current`, not
        // `aria-pressed`: these are one-of-many choices, not toggles. tabIndex={-1}
        // because the portaled panel is outside the trigger's tab order; DropdownPanel
        // focuses an option on open and arrow keys move between them.
        <button
            type="button"
            data-dropdown-option=""
            data-selected={selected}
            aria-current={selected || undefined}
            // Unnamed, the option takes its name from everything inside it — a sentence-length
            // subtitle included.
            aria-labelledby={item.subtitle ? optionLabelId : undefined}
            aria-describedby={item.subtitle ? optionSubtitleId : undefined}
            tabIndex={-1}
            onClick={(e) => {
                e.stopPropagation();
                onItemSelected(item);
            }}
            className="malachite-dropdown-option"
            style={{
                minHeight: 38,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                padding: 10,
                gap: 12,
                cursor: 'pointer',
                transition: 'background-color 120ms ease',
                // Reset the UA button chrome. Background is deliberately absent — see PANEL_RULES.
                width: '100%',
                border: 'none',
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
            }}
        >
            {/* Spans: <button> takes phrasing content only. `minWidth: 0` so a long,
                unwrappable subtitle ellipsises instead of stretching the option. */}
            <span
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    ...(stacked ? { minWidth: 0 } : {}),
                }}
            >
                <span style={{ flex: 1, display: 'block', ...(stacked ? { minWidth: 0 } : {}) }}>
                    <span
                        style={
                            stacked
                                ? { display: 'flex', flexDirection: 'column', gap: 2 }
                                : { display: 'flex', alignItems: 'center', gap: 8 }
                        }
                    >
                        <LabelRow row={stacked && !!item.icon}>
                            {item.icon && (
                                <span
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    {item.icon}
                                </span>
                            )}
                            <span
                                id={item.subtitle ? optionLabelId : undefined}
                                style={{
                                    fontWeight: 400,
                                    color: textColor,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minWidth: 0,
                                }}
                            >
                                {item.label}
                            </span>
                        </LabelRow>
                        {item.subtitle &&
                            (stacked ? (
                                <span
                                    id={optionSubtitleId}
                                    ref={subtitleRef}
                                    style={{
                                        fontSize: ANNOTATION_FONT_SIZE,
                                        fontWeight: 400,
                                        lineHeight: 1.4,
                                        color: selected
                                            ? textColor
                                            : 'var(--malachite-text-dimmed, rgb(161, 161, 161))',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {item.subtitle}
                                </span>
                            ) : (
                                <>
                                    <span
                                        style={{
                                            fontSize: ANNOTATION_FONT_SIZE,
                                            fontWeight: 400,
                                            color: 'var(--malachite-text-secondary, rgb(123, 123, 123))',
                                            flexShrink: 0,
                                        }}
                                    >
                                        |
                                    </span>
                                    <span
                                        id={optionSubtitleId}
                                        style={{
                                            fontSize: ANNOTATION_FONT_SIZE,
                                            fontWeight: 400,
                                            color: 'var(--malachite-text-secondary, rgb(123, 123, 123))',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            minWidth: 0,
                                        }}
                                    >
                                        {item.subtitle}
                                    </span>
                                </>
                            ))}
                    </span>
                </span>
                <span
                    style={{
                        // Reserved only when there is something to put there: an empty slot
                        // still holding its width truncates the label and subtitle early.
                        minWidth: item.rightSideText
                            ? 'var(--malachite-text-right-side-text-min-width, 80px)'
                            : 0,
                        flexShrink: 1,
                        display: 'flex',
                        justifyContent: rightSideJustify,
                    }}
                >
                    {item.rightSideText && (
                        <span
                            style={{
                                fontSize: 'var(--malachite-text-right-side-text-font-size, 12px)',
                                fontWeight: 400,
                                color: rightSideTextColor,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                            }}
                        >
                            {item.rightSideText}
                        </span>
                    )}
                </span>
            </span>
        </button>
    );

    // Focus lands on the option itself, and focusin does not reach a descendant, so a keyboard
    // user could never open a tooltip mounted inside it.
    return stacked ? (
        <Tooltip
            text={item.subtitle}
            shouldShow={isSubtitleTruncated}
            isShrinkable
            describesTrigger={false}
        >
            {option}
        </Tooltip>
    ) : (
        option
    );
};

interface DropdownPanelProps {
    items: DropdownItem[];
    onItemSelected: (item?: DropdownItem) => void;
    selectedItem: DropdownItem | undefined;
    menuLoading?: boolean;
    emptyMessage: string;
    /** Focus an option on open. False in search mode, where the input keeps focus. */
    autoFocus: boolean;
    /** Panel has been measured. It renders `visibility: hidden` until then, and a
     *  hidden element cannot take focus, so open-time focus has to wait for this. */
    positioned: boolean;
    /** Close the panel and hand focus back to the trigger. */
    onDismiss: () => void;
    subtitleLayout: SubtitleLayout;
    size: DropdownSize;
}

const DropdownPanel = React.forwardRef<
    HTMLDivElement,
    DropdownPanelProps & { style?: React.CSSProperties }
>((props, ref) => {
    const {
        items,
        onItemSelected,
        selectedItem,
        menuLoading,
        emptyMessage,
        autoFocus,
        positioned,
        onDismiss,
        subtitleLayout,
        size,
        style,
    } = props;
    const panelRef = useRef<HTMLDivElement | null>(null);

    // The panel only mounts while open, so no condition. useInsertionEffect, not layout:
    // it runs before the DOM mutation, so the rules are in place before the rows are inserted.
    useInsertionEffect(() => {
        ensureDropdownPanelStyles();
    }, []);

    useTopLayer(panelRef, true);

    // Start on the selected option so Enter re-confirms the current value. Not keyed on
    // `items`: re-running as the filtered list changes would yank focus back to the top.
    useEffect(() => {
        if (!autoFocus || !positioned) {
            return;
        }
        const options = panelOptions(panelRef.current);
        const selected = options.find((option) => option.dataset.selected === 'true');
        // Falls back to the panel: with no options nothing else can hold focus, and Tab-dismiss
        // only fires from inside it.
        (selected ?? options[0] ?? panelRef.current)?.focus();
    }, [autoFocus, positioned]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        // The panel is portaled to document.body, so the browser's next tab stop bears no
        // relation to the trigger. Dismissing restores trigger focus so Tab resumes there.
        if (event.key === 'Tab') {
            onDismiss();
            return;
        }
        const options = panelOptions(panelRef.current);
        if (options.length === 0) {
            return;
        }
        const current = options.indexOf(document.activeElement as HTMLElement);
        const target = {
            ArrowDown: current + 1,
            ArrowUp: current - 1,
            Home: 0,
            End: options.length - 1,
        }[event.key];
        if (target === undefined) {
            return;
        }
        event.preventDefault();
        options[Math.min(Math.max(target, 0), options.length - 1)]?.focus();
    };

    // Inline styles, not Tailwind: this portals into document.body, outside the
    // Shadow DOM the malachite stylesheet is adopted into.
    return (
        <div
            ref={(node) => {
                panelRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    ref.current = node;
                }
            }}
            {...overlayIdentity('dropdown')}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            style={{
                background: MENU_SPECS.standard.background,
                border: MENU_SPECS.standard.border,
                borderRadius: MENU_SPECS.standard.radius,
                boxShadow: MENU_SPECS.standard.shadow,
                maxHeight: MENU_MAX_HEIGHT_PX,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                fontSize: CONTROL_TEXT_SIZE[size],
                ...style,
            }}
        >
            {menuLoading ? (
                <div style={PANEL_MESSAGE_STYLE}>
                    <Spinner size="xxxsmall" />
                    <span>Loading…</span>
                </div>
            ) : items.length === 0 ? (
                <div style={PANEL_MESSAGE_STYLE}>{emptyMessage}</div>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        padding: MENU_SPECS.standard.padding,
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor:
                            'var(--malachite-text-placeholder, rgb(187, 187, 187)) transparent',
                    }}
                >
                    {items.map((item, index) => {
                        const prev = items[index - 1];
                        const showGroupHeader =
                            !!item.group && (!prev || prev.group !== item.group);
                        return (
                            <div key={item.id} style={{ flexShrink: 0 }}>
                                {showGroupHeader && (
                                    <div
                                        style={{
                                            padding: '6px 10px 4px',
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: 'var(--malachite-text-secondary, rgb(123, 123, 123))',
                                            textTransform: 'none',
                                            userSelect: 'none',
                                        }}
                                    >
                                        {item.group}
                                    </div>
                                )}
                                <DropdownItemComponent
                                    item={item}
                                    onItemSelected={onItemSelected}
                                    subtitleLayout={subtitleLayout}
                                    selected={item.id === selectedItem?.id || false}
                                />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});
DropdownPanel.displayName = 'DropdownPanel';

export const Dropdown = ({
    items,
    onItemSelected,
    defaultValue,
    value,
    disabled = false,
    error,
    tone,
    label,
    hint,
    rightSideContent,
    tooltip,
    name,
    required = false,
    requiredIndicator,
    loading,
    menuLoading,
    formControl,
    description,
    helperText,
    placeholder,
    search = false,
    showSubtitleInTrigger = false,
    subtitleLayout = 'inline',
    clearable = false,
    size = 'large',
    fullWidth = false,
    width,
    minWidth,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
}: DropdownProps) => {
    const { controlId, labelId, descriptionId, hintId, helperTextId } = useFormFieldIds({
        id,
        label,
        description,
        hint,
        helperText: true,
    });
    const [isOpen, setIsOpen] = useState(false);
    // The id, never the resolved item: a cached object goes stale when `items` is rebuilt.
    const [ownValue, setOwnValue] = useState(defaultValue);
    const selectedId = value ?? ownValue;
    const selectedItem = items.find((item) => item.id === selectedId);
    const showClear = clearable && selectedId !== undefined && selectedId !== '' && !disabled;
    const triggerIsStacked = showSubtitleInTrigger && !search && !!selectedItem?.subtitle;
    const clearLabel = `Clear ${label ?? placeholder ?? 'selection'}`;

    // `defaultValue` only seeds the state, so a later change has to be pushed in.
    const lastDefaultValueRef = useRef(defaultValue);
    useEffect(() => {
        if (lastDefaultValueRef.current === defaultValue) {
            return;
        }
        lastDefaultValueRef.current = defaultValue;
        setOwnValue(defaultValue);
    }, [defaultValue]);
    const [searchQuery, setSearchQuery] = useState('');
    // Search mode only: set when the panel is opened by ArrowDown, so focus follows the
    // user into the list on mount instead of staying in the input.
    const [focusPanelOnOpen, setFocusPanelOnOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [panelPosition, setPanelPosition] = useState<MenuGeometry | null>(null);

    const reopenOnFocusRef = useRef(true);

    // In search mode the trigger is a wrapper div; the focusable part is the input.
    const focusTrigger = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) {
            return;
        }
        (trigger instanceof HTMLButtonElement
            ? trigger
            : trigger.querySelector<HTMLElement>('input')
        )?.focus();
    }, []);

    const handleClear = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            setOwnValue(undefined);
            // The resolver is told the empty this form started the field at; the public
            // callback keeps reporting `undefined` whatever shape that is.
            reportSelectionRef.current?.(reportEmptyRef.current);
            onItemSelected?.(undefined);

            if (search) {
                reopenOnFocusRef.current = false;
            }
            focusTrigger();
        },
        [onItemSelected, search, focusTrigger],
    );

    const updatePanelPosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        setPanelPosition(
            measureMenuGeometry(panelRef.current, {
                triggerRect: trigger.getBoundingClientRect(),
                preset: 'standard',
                minWidth,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
            }),
        );
    }, [minWidth]);

    useEffect(() => {
        if (!isOpen) {
            setPanelPosition(null);
            setFocusPanelOnOpen(false);
            return;
        }
        updatePanelPosition();
        const handleClickOutside = (event: MouseEvent) => {
            const path = event.composedPath();
            const trigger = dropdownRef.current;
            const panel = panelRef.current;
            const inTrigger = !!trigger && path.includes(trigger);
            const inPanel = !!panel && path.includes(panel);
            if (!inTrigger && !inPanel) {
                setIsOpen(false);
                if (search) {
                    setSearchQuery('');
                }
                // Closing unmounts whatever option held focus, so the focusout the wrapper
                // below listens for never fires. Clicking away from an open menu is the one
                // way of leaving the field that has to report the blur itself.
                reportBlurRef.current?.();
            }
        };
        // ESC via the shared layer stack: closes only the topmost overlay. Focus is
        // restored explicitly — unmounting the option it sits on would drop it to <body>.
        const popEscapeLayer = pushEscapeLayer(() => {
            setIsOpen(false);
            if (search) {
                setSearchQuery('');
            }
            focusTrigger();
        });
        const handleScrollOrResize = () => updatePanelPosition();

        // `scroll` is not composed: a window listener never hears a scroller inside a
        // consumer's shadow root, so the trigger's rect is tracked instead.
        let frame = 0;
        let lastRect = '';
        const followTrigger = () => {
            const trigger = triggerRef.current;
            if (trigger) {
                const rect = trigger.getBoundingClientRect();

                if (isTriggerOffscreen(rect, window.innerWidth, window.innerHeight)) {
                    setIsOpen(false);
                    if (search) {
                        setSearchQuery('');
                    }
                    return;
                }

                const signature = rectSignature(rect);
                if (signature !== lastRect) {
                    lastRect = signature;
                    updatePanelPosition();
                }
            }
            frame = requestAnimationFrame(followTrigger);
        };
        frame = requestAnimationFrame(followTrigger);

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        return () => {
            cancelAnimationFrame(frame);
            document.removeEventListener('mousedown', handleClickOutside);
            popEscapeLayer();
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [isOpen, search, updatePanelPosition, focusTrigger]);

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        updatePanelPosition();

        const panel = panelRef.current;
        if (!panel || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => updatePanelPosition());
        observer.observe(panel);
        return () => observer.disconnect();
    }, [isOpen, updatePanelPosition]);

    const reportSelectionRef = useRef<((value: unknown) => void) | undefined>(undefined);
    const reportBlurRef = useRef<(() => void) | undefined>(undefined);
    const reportEmptyRef = useRef<unknown>('');

    const handleItemSelected = (item?: DropdownItem) => {
        setOwnValue(item?.id);
        reportSelectionRef.current?.(item?.id ?? reportEmptyRef.current);
        setIsOpen(false);
        if (search) {
            setSearchQuery('');
            // Focus returns to the search input, whose focus handler would reopen the panel.
            reopenOnFocusRef.current = false;
        }
        onItemSelected?.(item?.id);
        focusTrigger();
    };

    const dismissPanel = () => {
        setIsOpen(false);
        if (search) {
            setSearchQuery('');
        }
        focusTrigger();
    };

    // ArrowDown is the "take me into the list" gesture. When it also opens the panel the
    // intent carries over to the mount, so it doesn't cost a second press in search mode.
    const enterPanel = () => {
        const first = panelOptions(panelRef.current)[0];
        if (first) {
            first.focus();
            return;
        }
        setFocusPanelOnOpen(true);
        setIsOpen(true);
    };

    const visibleItems =
        search && searchQuery ? items.filter((item) => matchesSearch(item, searchQuery)) : items;

    return (
        <BaseFormComponent
            fullWidth={fullWidth}
            label={label}
            hint={hint}
            rightSideContent={rightSideContent}
            tooltip={tooltip}
            name={name}
            required={required}
            requiredIndicator={requiredIndicator}
            loading={loading}
            formControl={formControl}
            description={description}
            controlId={controlId}
            labelId={labelId}
            descriptionId={descriptionId}
            hintId={hintId}
            loadingComponent={
                width === undefined ? (
                    <LoadingInput
                        heightClass={TRIGGER_SIZE_HEIGHT[size]}
                        helperText={!!helperText}
                        helperTextMarginTop
                    />
                ) : (
                    <div style={controlWidthStyle(width)}>
                        <LoadingInput
                            heightClass={TRIGGER_SIZE_HEIGHT[size]}
                            helperText={!!helperText}
                            helperTextMarginTop
                        />
                    </div>
                )
            }
        >
            {({ error: fieldError, message, onFieldChange, onFieldBlur, emptyValue }) => {
                reportSelectionRef.current = onFieldChange;
                reportBlurRef.current = onFieldBlur;
                reportEmptyRef.current = emptyValue;
                const isError = error ?? fieldError;
                // `error={false}` opts out of the resolver entirely, message included: a screen
                // that has dealt with the rejection its own way would otherwise keep reporting it.
                // The message is its own line rather than a replacement for the helper text,
                // matching Input and TextArea: a field that explains what it wants still has to
                // say why what was chosen was rejected.
                const errorMessage = error === false ? undefined : message;
                const errorMessageId = `${helperTextId}-error`;
                const describedBy =
                    [
                        descriptionId,
                        hintId,
                        helperText ? helperTextId : undefined,
                        errorMessage ? errorMessageId : undefined,
                    ]
                        .filter(Boolean)
                        .join(' ') || undefined;

                return (
                    <>
                        <div
                            ref={dropdownRef}
                            className={`relative w-full`}
                            style={controlWidthStyle(width)}
                            // Only leaving the field altogether is a blur `mode: 'onBlur'` should
                            // hear. The panel is portaled to the body, so containment has to be
                            // asked of it separately — otherwise opening the menu reads as
                            // leaving, and the form validates before anything has been chosen.
                            onBlur={(event) => {
                                const next = event.relatedTarget as Node | null;
                                const staysInField =
                                    event.currentTarget.contains(next) ||
                                    panelRef.current?.contains(next) === true;

                                if (!staysInField) {
                                    reportBlurRef.current?.();
                                }
                            }}
                        >
                            {showClear && !search && (
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    aria-label={clearLabel}
                                    className={`absolute ${
                                        triggerIsStacked
                                            ? CLEAR_STACKED_TOP_CLASSES[size]
                                            : 'top-1/2 -translate-y-1/2'
                                    } z-10 flex items-center
                            justify-center rounded-full text-[var(--malachite-text-placeholder)]
                            hover:text-[var(--malachite-text-primary)] transition-colors
                            focus-visible:[outline:2px_solid_var(--malachite-text-primary)] focus-visible:outline-offset-2 ${CLEAR_OFFSET_CLASSES[size]}`}
                                >
                                    <CloseIcon size={13} />
                                </button>
                            )}
                            <DropdownTrigger
                                hasClear={showClear}
                                onClear={handleClear}
                                clearLabel={clearLabel}
                                ref={triggerRef as React.Ref<HTMLElement>}
                                isOpen={isOpen}
                                onClick={() => !disabled && setIsOpen(!isOpen)}
                                selectedItem={selectedItem}
                                disabled={disabled}
                                error={isError}
                                tone={tone}
                                placeholder={placeholder}
                                search={search}
                                showSubtitleInTrigger={showSubtitleInTrigger}
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                onFocusOpen={() => {
                                    if (!reopenOnFocusRef.current) {
                                        reopenOnFocusRef.current = true;
                                        return;
                                    }
                                    setIsOpen(true);
                                }}
                                onArrowDown={() => {
                                    if (!disabled) {
                                        enterPanel();
                                    }
                                }}
                                size={size}
                                controlId={controlId}
                                hasCallerId={!!id}
                                labelId={labelId}
                                describedBy={describedBy}
                                required={required}
                                ariaLabel={ariaLabel}
                                ariaLabelledBy={ariaLabelledBy}
                            />
                            {isOpen &&
                                createPortal(
                                    <DropdownPanel
                                        ref={panelRef}
                                        style={{
                                            position: 'fixed',
                                            top: panelPosition?.top ?? 0,
                                            left: panelPosition?.left ?? 0,
                                            width: panelPosition?.width,
                                            maxHeight: panelPosition?.maxHeight,
                                            visibility: panelPosition ? 'visible' : 'hidden',
                                            opacity: panelPosition ? 1 : 0,
                                        }}
                                        items={visibleItems}
                                        onItemSelected={handleItemSelected}
                                        selectedItem={selectedItem}
                                        menuLoading={menuLoading}
                                        emptyMessage={
                                            items.length === 0
                                                ? 'No options available'
                                                : 'No results found'
                                        }
                                        subtitleLayout={subtitleLayout}
                                        size={size}
                                        autoFocus={!search || focusPanelOnOpen}
                                        positioned={panelPosition !== null}
                                        onDismiss={dismissPanel}
                                    />,
                                    document.body,
                                )}
                        </div>
                        {helperText && (
                            <div
                                id={helperTextId}
                                className="mt-1 text-[length:var(--malachite-font-size-small,13px)] text-left pl-2 text-[var(--malachite-text-dimmed,rgb(161,161,161))]"
                            >
                                {helperText}
                            </div>
                        )}
                        <FieldMessage id={errorMessageId} message={errorMessage} spaced />
                    </>
                );
            }}
        </BaseFormComponent>
    );
};

export default Dropdown;
